/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import {
  getPoaDataToSign,
  useCurrentUser,
  useGetMRFList,
  useVersionOf,
} from '@builddocs/data-management'
import { useToast } from '@builddocs/system'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material'
import format from 'date-fns/format'

import {
  isProxyValid,
  isUserMatchesCertificate,
  useIsProxyRequired,
} from '../utils'

import { CertificateBlock, UserMismatchError } from './atoms'
import { ProxySelector } from './atoms/Proxy'
import CertificateInfo from './CertificateInfo'
import { cryptopro, initCrypto } from './cryptopro/inits'
import { CertificateInList, CryptoproCertificate } from './cryptopro/types'
import { FaceControlContext } from './FaceControlContext'

interface SignDataDialogProps {
  onClose: () => void
  isOpen: boolean
  isVerifyOnly: boolean
  isLoading: boolean
}

/**
 * Диалог для подписи одним только ключом, без лица.
 * Верификация ключа не должна происходить. Человек имеет право подписывать
 * любым своим ключом
 * @param {SignDataDialogProps} props
 * @returns {JSX.Element}
 * @constructor
 */
function SignDataDialog(props: SignDataDialogProps) {
  const { onClose, isOpen, isVerifyOnly, isLoading } = props
  const [error, setError] = useState<Error>()
  const { toast } = useToast()
  const { user } = useCurrentUser()

  const [certs, setCerts] = useState<CertificateInList[]>([])
  const [certSelected, setCertSelected] = useState<CryptoproCertificate>()
  const [selectedCertId, setSelectedCertId] = useState<string | undefined>()
  const faceControl = useContext(FaceControlContext)
  const isMountedRef = useRef(true)
  const isFetchingCertsRef = useRef(false)
  const fetchedOnOpenRef = useRef(false)
  const pendingCertIdRef = useRef<string | null>(null)

  const { poaIsEnabled } = useVersionOf()
  const { data: proxyList } = useGetMRFList({
    userId: user.userId,
    enabled: poaIsEnabled,
  })
  const [selectedProxyId, setSelectedProxyId] = useState<string | null>(null)

  const selectedProxy = proxyList?.poas.find(
    item => item.poaIdInternal === selectedProxyId,
  )

  const getCertInfo = useCallback((certID: string) => {
    setSelectedCertId(certID)
    pendingCertIdRef.current = certID
    initCrypto()
      .then(() => cryptopro.certificateInfo(certID))
      .then((info: CryptoproCertificate) => {
        if (!isMountedRef.current) return
        if (pendingCertIdRef.current !== certID) return
        setCertSelected(info)
      })
      .catch(() => undefined)
  }, [])

  const getCertsList = useCallback(async () => {
    if (isFetchingCertsRef.current) return
    isFetchingCertsRef.current = true
    try {
      await initCrypto()
      const list = await cryptopro.listCertificates()

      if (!isMountedRef.current) return
      setCerts(list)
      if (list && list[0]) {
        setSelectedCertId(list[0].id)
        getCertInfo(list[0].id)
      }
    } catch (e) {
      toast.error('Ошибка при получении списка сертификатов')
    } finally {
      isFetchingCertsRef.current = false
    }
  }, [getCertInfo, toast])

  useEffect(() => {
    if (isOpen && !fetchedOnOpenRef.current) {
      fetchedOnOpenRef.current = true
      getCertsList()
    }
    if (!isOpen) {
      fetchedOnOpenRef.current = false
    }
  }, [isOpen, getCertsList])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const signEntity = () => {
    if (!certSelected) return

    // setIsLoading(true)
    const certificateDateStart = format(
      certSelected.ValidFromDate,
      'yyyy-MM-dd',
    )
    const certificateDateEnd = format(certSelected.ValidToDate, 'yyyy-MM-dd')
    const poaData = getPoaDataToSign({
      proxyId: selectedProxy?.poaIdInternal,
      poa: selectedProxy,
      poaIsEnabled,
    })

    faceControl.signData({
      certSelected,
      keySerialNumber: certSelected.SerialNumber,
      certificateDateStart,
      certificateDateEnd,
      signerName: `${certSelected.Subject.SN} ${certSelected.Subject.G}`,
      ...poaData,
      //        onFailure: () => {
      //          setIsSuccess(false)
      //          // setIsLoading(false)
      //        },
      //        onSuccess: () => {
      //          setIsSuccess(true)
      //          // setIsLoading(false)
      //        },
    })
  }

  // removed duplicate fetch effect; handled in the open-effect above

  const allowSend = certSelected && certSelected?.IsValid

  // Проверка совпадения ФИО пользователя с ФИО в сертификате
  const { isValid: isUserMatchesCert } = isUserMatchesCertificate(
    user,
    certSelected,
  )

  const { isRequired: isProxyRequiredFlag } = useIsProxyRequired(certSelected)
  const { isValidProxy, errorMessage } = isProxyValid(
    selectedProxy,
    certSelected,
  )

  // Проверяем валидность: если МЧД требуется, то она должна быть выбрана и валидна
  const isProxyAndCertValid =
    certSelected &&
    (!isProxyRequiredFlag || (selectedProxy && isValidProxy)) &&
    (!selectedProxy || isValidProxy)

  const handleClose = () => {
    !isLoading && onClose()
  }

  const primaryActionTitle = isVerifyOnly
    ? isLoading
      ? 'Верификация...'
      : 'Продолжить'
    : isLoading
    ? 'Обработка подписи...'
    : 'Подписать запись'

  return (
    <Dialog fullWidth maxWidth="sm" open={isOpen} onClose={handleClose}>
      <DialogTitle>Подписание</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <CertificateBlock
            disableSignAction
            actionTitle={primaryActionTitle}
            certs={certs}
            disabled={!allowSend || isLoading}
            dismissError={() => setError(undefined)}
            error={
              error?.message ||
              (!isUserMatchesCert ? <UserMismatchError /> : undefined)
            }
            getCertInfo={getCertInfo}
            infoMessage="Подпись можно сделать только валидным ключом"
            loading={isLoading}
            selectedCertificate={certSelected}
            selectedCertificateId={selectedCertId}
            updateCertsList={getCertsList}
            onClick={signEntity}
          />
          {certSelected && <CertificateInfo certificate={certSelected} />}
          {certs?.length && poaIsEnabled ? (
            <ProxySelector
              errorMessage={errorMessage}
              isProxyRequired={isProxyRequiredFlag}
              isValid={isProxyAndCertValid}
              proxy={proxyList?.poas}
              selectedProxy={selectedProxy}
              selectedProxyId={selectedProxyId}
              onSelect={setSelectedProxyId}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={handleClose}>
          Отмена
        </Button>
        <LoadingButton
          disabled={
            !allowSend ||
            isLoading ||
            !isProxyAndCertValid ||
            !isUserMatchesCert
          }
          id="face-control-dialog-button-action"
          loading={isLoading}
          variant="contained"
          onClick={signEntity}
        >
          Подписать
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default SignDataDialog
